import { Button, Checkbox, Empty, List, Modal, Typography } from 'antd';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Offer } from '../../models/offers.model';
import { WantedListing } from '../../models/wantedListings.model';

const { Text } = Typography;

interface WantedListingsMatchModalProps {
  visible: boolean;
  matchingListings: WantedListing[];
  offer: Offer;
  onClose: () => void;
  onSubmit: (selectedListings: string[]) => Promise<void>;
}

export const WantedListingsMatchModal: React.FC<WantedListingsMatchModalProps> = ({
  visible,
  matchingListings,
  offer,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation('wanted-listings');
  const [selectedListings, setSelectedListings] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const handleCheckboxChange = (listingId: string, checked: boolean) => {
    if (checked) {
      setSelectedListings([...selectedListings, listingId]);
    } else {
      setSelectedListings(selectedListings.filter((id) => id !== listingId));
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await onSubmit(selectedListings);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={t('MODAL.TITLE')}
      open={visible}
      onCancel={onClose}
      width={600}
      footer={[
        <Button key="skip" onClick={onClose}>
          {t('MODAL.SKIP')}
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={handleSubmit}
          disabled={selectedListings.length === 0}>
          {t('MODAL.CONFIRM')}
        </Button>,
      ]}>
      <div className="mb-4">
        <Text>
          {t('MODAL.DESCRIPTION')} <strong>{offer.name}</strong>.
        </Text>
      </div>

      {matchingListings.length === 0 ? (
        <Empty description={t('MODAL.NO_MATCHES')} />
      ) : (
        <List
          dataSource={matchingListings}
          renderItem={(listing) => (
            <List.Item key={listing.id} className="border rounded-lg p-4 mb-2">
              <Checkbox
                checked={selectedListings.includes(listing.id)}
                onChange={(e) => handleCheckboxChange(listing.id, e.target.checked)}
                className="w-full ml-4">
                <div className="ml-2">
                  <div className="font-medium">{listing.location}</div>
                  {listing.maxRent !== undefined && (
                    <div className="text-sm text-gray-500">{t('MODAL.RENT_RANGE', { maxRent: listing.maxRent })}</div>
                  )}
                  {listing.minSurface !== undefined && (
                    <div className="text-sm text-gray-500">
                      {t('MODAL.SURFACE_MIN', { minSurface: listing.minSurface })}
                    </div>
                  )}
                  {listing.description && <div className="text-sm text-gray-500 mt-1">{listing.description}</div>}
                </div>
              </Checkbox>
            </List.Item>
          )}
        />
      )}

      <div className="mt-4 text-sm text-gray-500">
        <Text type="secondary">{t('MODAL.SELECT_INFO')}</Text>
      </div>
    </Modal>
  );
};
